import React, { useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import SkeletonDetails from "Components/Generic/LoadingSkeleton";
import SideViewComponent, { Navigation, PropHandles } from "Components/Generic/ViewJobComponent";
import { StorageBox } from "Models/storage.box";
import { DetailsTab } from "./Details";
import { useQuery } from "@tanstack/react-query";
import StorageBoxService from "Services/storage.box.service";

interface Props {
  storageBox?: StorageBox;
  sideViewRef: React.RefObject<PropHandles>;
  loading: boolean;
}

export const StorageBoxViewModal = ({ storageBox, sideViewRef, loading }: Props) => {
  const { t: translate } = useTranslation();

  const [storageBoxWithJobAndItemsJobs, setStorageBoxWithJobAndItemsJobs] = useState<StorageBox>();

  const { isFetching } = useQuery(
    ["storage.box.with.items.jobs.and.jobs", storageBox?.id],
    () =>
      StorageBoxService.getById(Number(storageBox?.id || 0), {
        include: JSON.stringify([
          {
            association: "storageBoxType",
            attributes: ["id", "name"],
          },
          {
            association: "warehouses",
            attributes: ["id", "name"],
          },
          {
            association: "branches",
            attributes: ["id", "name"],
          },
          {
            association: "itemsJobs",
            attributes: ["id", "jobId"],
            include: {
              association: "job",
              attributes: ["id", "reference", "updatedAt"],
              include: {
                association: "customer",
                attributes: ["id", "fullName"],
              },
            },
          },
          {
            association: "jobs",
            attributes: ["id", "reference", "updatedAt"],
            include: {
              association: "customer",
              attributes: ["id", "fullName"],
            },
          },
        ]),
      }),
    {
      enabled: !!storageBox?.id,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setStorageBoxWithJobAndItemsJobs(response.data);
      },
    }
  );

  const handleSetTabs = useCallback(() => {
    const tabs = [
      {
        key: Navigation.DETAILS,
        loading,
        isEmpty: !storageBoxWithJobAndItemsJobs,
        render: () => <DetailsTab value={storageBoxWithJobAndItemsJobs} />,
        renderLoading: () => <SkeletonDetails />,
      },
    ];

    return tabs;
  }, [storageBoxWithJobAndItemsJobs]);

  const handleSetNavigation = useCallback(() => {
    const navList = [{ key: Navigation.DETAILS }];

    return navList;
  }, [storageBoxWithJobAndItemsJobs]);

  const getTitle = useCallback(() => {
    if (storageBoxWithJobAndItemsJobs?.number)
      return `${translate("storageBox")} - ${storageBoxWithJobAndItemsJobs?.number}`;

    return translate("storageBox");
  }, [storageBoxWithJobAndItemsJobs]);

  return (
    <SideViewComponent
      ref={sideViewRef}
      job={storageBoxWithJobAndItemsJobs}
      isLoading={loading || isFetching}
      title={getTitle()}
      navigation={handleSetNavigation()}
      tabs={handleSetTabs()}
    />
  );
};
